import { useFormik } from "formik";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import InputCustom from "../../../../../../components/InputCustom";
import SelectCustom from "../../../../../../components/SelectCustom";
import Switch from "../../../../../../components/Switch";
import Loading from "../../../../../../core/common/Loading";
import api from "../../../../../../services/api";
import { IGrupo } from "../types/types";
import * as Yup from "yup";

interface FormValues {
  nome: string;
  subsistema: string;
  identificacao_biometrica: boolean;
  ativo: boolean;
  justificativa: string;
  permissions: Record<string, boolean>;
}

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: IGrupo;
  onConfirm: () => void;
  onClose: () => void;
}

const permissionsData = [
  {
    category: "Cadastros",
    modules: [
      { name: "Terminais", key: "terminais" },
      { name: "Proprietários de Carga", key: "proprietarios_carga" },
      { name: "Transportadoras", key: "transportadoras" },
      { name: "Motoristas", key: "motoristas" },
      { name: "Veículos", key: "veiculos" },
      { name: "Mensalista", key: "mensalistas" },
    ]
  },
  {
    category: "Comercial",
    modules: [
      { name: "Custo Triagem", key: "custo_triagem" },
      { name: "Custo Estadia", key: "custo_estadia" },
      { name: "Bumerangue", key: "bumerangue" },
    ]
  },
  {
    category: "Operações no Porto",
    modules: [
      { name: "Agendada", key: "agendada" },
      { name: "Carrossel", key: "carrossel", extraActions: [{ label: "Finalizar", key: "FINALIZAR" }] },
    ]
  },
  {
    category: "Operações no Pátio",
    modules: [
      { name: "Entrada e Saída de Veículos", key: "entrada_saida_veiculos" },
      { name: "Serviços", key: "servicos" },
      { name: "Triagens", key: "triagens" },
      { name: "Faturamento", key: "faturamento_patio" },
      { name: "Estadias", key: "estadias" },
      { name: "Cancelas", key: "cancelas" },
      { name: "Cancela Manual", key: "cancela_manual" },
      { name: "Guichês", key: "guiches" },
      { name: "Painel de Chamadas", key: "painel_chamadas" },
      { name: "Entrada e Saída de Veículos - Guarita", key: "entrada_saida_veiculos_guarita" },
    ]
  },
  {
    category: "SPE",
    modules: [
      { name: "Agendada SPE", key: "agendada_spe" },
      { name: "Entrada e Saída de Veículos SPE", key: "entrada_saida_veiculos_spe" },
      { name: "Triagens SPE", key: "operacao_patio_spe" },
      { name: "Cancela Manual SPE", key: "cancela_manual_spe" },
      { name: "Tipo de Carga SPE", key: "tipos_carga_spe" },
    ]
  },
  {
    category: "Financeiro",
    modules: [
      {
        name: "Caixa Geral",
        key: "caixa_geral",
        extraActions: [
          { label: "Adicionar depósito", key: "DEPOSITAR" },
          { label: "Adicionar retirada", key: "RETIRAR" },
          { label: "Editar total no cofre", key: "EDITARCOFRE" },
        ],
      },
      { name: "Faturas", key: "faturas" },
      { name: "Movimentações", key: "movimentacoes", extraActions: [{ label: "Finalizar", key: "FINALIZAR" }, { label: "Visualizar Estornos", key: "ESTORNADOS" }] },
      { name: "Faturamento", key: "faturamento_financeiro", extraActions: [{ label: "Finalizar", key: "FINALIZAR" }] },
    ]
  },
  {
    category: "Auditoria",
    modules: [
      { name: "API - Pátio para Porto", key: "api_patio_porto", allowedActions: ["ACESSAR", "CONHECER", "EXPORTAR"] },
      { name: "Entrada Veículos", key: "auditoria_entrada_veiculos", allowedActions: ["ACESSAR", "CONHECER", "EXPORTAR"] },
      { name: "Terminal Pátio", key: "auditoria_terminal_patio", allowedActions: ["ACESSAR", "CONHECER", "EXPORTAR"] },
    ]
  },
  {
    category: "Configurações",
    modules: [
      { name: "Usuários", key: "usuarios" },
      { name: "Grupos", key: "grupos" },
      { name: "Parâmetros", key: "parametros" },
      { name: "Validação do CRLV", key: "validacao_crlv" },
      { name: "Tipos de Serviço", key: "tipos_servico" },
    ]
  },
  {
    category: "Indicadores",
    modules: [
      { name: "Power BI", key: "power_bi", allowedActions: ["ACESSAR"] },
    ]
  },
  {
    category: "Servidor",
    modules: [
      { name: "Servidor", key: "servidor", allowedActions: ["ACESSAR"] },
    ]
  },
];

const defaultPermissionTypes = [
  { label: "Acessar", key: "ACESSAR" },
  { label: "Salvar", key: "SALVAR" },
  { label: "Remover", key: "REMOVER" },
  { label: "Conhecer", key: "CONHECER" },
  { label: "Auditar", key: "AUDITAR" },
  { label: "Exportar", key: "EXPORTAR" },
];

const Form: React.FC<Props> = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);

  const validationSchema = Yup.object().shape({
    nome: Yup.string().required("Campo obrigatório"),
    subsistema: Yup.string().required("Campo obrigatório"),
  });

  const handleSubmit = useCallback(async (values: FormValues) => {
    try {
      setLoading(true);
      
      const body = {
        id_grupo: props.selectedRow?.id_grupo,
        nome: values.nome,
        subsistema: values.subsistema,
        identificacao_biometrica: values.identificacao_biometrica,
        ativo: values.ativo,
        justificativa: values.justificativa,
        permissions: values.permissions,
      };

      if (props.isEdit) {
        await api.post("/editar/grupos", body);
      } else {
        await api.post("/cadastrar/grupos", body);
      }

      setLoading(false);
      props.onConfirm();
    } catch (error: any) {
      setLoading(false);
      console.error("Erro ao salvar grupo:", error);
      alert("Erro ao salvar grupo: " + (error.response?.data?.message || error.message));
    }
  }, [props]);

  const initialValues: FormValues = useMemo(() => {
    const perms: Record<string, boolean> = {};
    permissionsData.forEach(cat => {
        cat.modules.forEach((mod: { key: string; allowedActions?: string[]; extraActions?: { label: string; key: string }[] }) => {
            const allowed = mod.allowedActions;
            const actions = allowed 
                ? defaultPermissionTypes.filter(pt => allowed.includes(pt.key))
                : defaultPermissionTypes;
            
            const allActions = mod.extraActions 
                ? [...actions, ...mod.extraActions]
                : actions;

            allActions.forEach(pt => {
                perms[`${mod.key}_${pt.key}`] = false; 
            });
        });
    });

    return {
      nome: props.selectedRow?.nome || "",
      subsistema: props.selectedRow?.subsistema || "",
      identificacao_biometrica: props.selectedRow?.identificacao_biometrica || false,
      ativo: props.selectedRow?.ativo !== undefined ? props.selectedRow?.ativo : true,
      justificativa: props.selectedRow?.justificativa || "",
      permissions: perms,
    };
  }, [props.selectedRow]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (props.isEdit && props.selectedRow?.id_grupo) {
      setLoading(true);
      console.log("DEBUG: Fetching permissions for group", props.selectedRow.id_grupo);
      api.post("/listar/grupos", { 
          id_grupo: props.selectedRow.id_grupo,
          qtd_por_pagina: 1 
      })
      .then((response) => {
          console.log("DEBUG: API Response", response.data);
          const data = response.data;
          const group = Array.isArray(data) ? data[0] : (data.data ? data.data[0] : null);
          console.log("DEBUG: Group object", group);
          
          if (group && group.grupos_permissoes) {
              console.log("DEBUG: grupos_permissoes found", group.grupos_permissoes);
              const currentPermissions = { ...initialValues.permissions };
              
              group.grupos_permissoes.forEach((perm: { 
                  ativo: number | boolean; 
                  submodulo?: string; 
                  modulo?: string; 
                  acao?: string; 
                  permissoes?: { submodulo?: string; modulo?: string; acao?: string };
                  permissao?: { submodulo?: string; modulo?: string; acao?: string };
              }) => {
                  // Verifica se a permissão está ativa para este grupo (campo ativo no relacionamento)
                  if (perm.ativo !== 1 && perm.ativo !== true) return;

                  // O backend retorna os detalhes da permissão dentro de um objeto "permissoes" (plural)
                  const detalhes = perm.permissoes || perm.permissao;

                  // O backend retorna a estrutura onde o "módulo" do front (ex: veiculos) é o "submodulo" no back
                  // E a "categoria" do front (ex: Cadastros) é o "modulo" no back.
                  // Portanto, devemos priorizar o submodulo para montar a chave correta.
                  const moduloNome = perm.submodulo || detalhes?.submodulo || perm.modulo || detalhes?.modulo;
                  const acaoNome = perm.acao || detalhes?.acao;
                  
                  if (moduloNome && acaoNome) {
                      const key = `${moduloNome.toLowerCase()}_${acaoNome.toUpperCase()}`;
                      if (Object.prototype.hasOwnProperty.call(currentPermissions, key)) {
                          console.log("DEBUG: Activating permission", key);
                          currentPermissions[key] = true;
                      } else {
                          console.warn("DEBUG: Permission key not found in frontend definitions", key);
                      }
                  } else {
                      console.warn("DEBUG: Could not determine module or action for permission", perm);
                  }
              });
              
              formik.setFieldValue("permissions", currentPermissions);
          } else {
              console.warn("DEBUG: grupos_permissoes not found in group object");
          }
      })
      .catch((error: unknown) => {
          console.error("Erro ao buscar permissões do grupo", error);
      })
      .finally(() => {
          setLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isEdit, props.selectedRow, initialValues.permissions]);

  const subsistemaOptions = [
    { value: "PATIO", label: "PÁTIO" },
    { value: "MOTORISTA", label: "MOTORISTA" },
    { value: "TERMINAL", label: "TERMINAL" },
    { value: "TRANSPORTADORA", label: "TRANSPORTADORA" },
    { value: "PROPRIETARIO_CARGA", label: "PROPRIETÁRIO CARGA" },
    { value: "PORTO", label: "PORTO" },
    { value: "COMERCIAL", label: "COMERCIAL" },
    { value: "FINANCEIRO", label: "FINANCEIRO" },
    { value: "AUDITORIA", label: "AUDITORIA" },
    { value: "CONFIGURACOES", label: "CONFIGURAÇÕES" },
    { value: "ADMINISTRADOR", label: "ADMINISTRADOR" },
    { value: "ENTRADA", label: "ENTRADA" },
    { value: "GERENTE", label: "GERENTE" },
  ];

  return (
    <div className="w-full bg-gray-50">
      <Loading loading={loading} skeletonFields={4} skeletonColumns={1} />
      <form onSubmit={formik.handleSubmit}>
        <div className="p-5" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
            
            {/* Identificação do Grupo */}
            <div className="mb-8">
                <div className="flex items-center mb-2">
                    <span className="text-blue-600 mr-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                        </svg>
                    </span>
                    <h3 className="text-lg font-bold text-gray-800">Identificação do Grupo</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4 ml-7">Defina os parâmetros principais para este perfil de acesso.</p>
                
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <SelectCustom
                                title="Subsistema"
                                data={subsistemaOptions}
                                value={formik.values.subsistema}
                                onChange={(value: { value: string; label: string }) => formik.setFieldValue("subsistema", value.value)}
                                error={formik.touched.subsistema && formik.errors.subsistema}
                                disabled={props.isView}
                            />
                        </div>
                        <div>
                            <InputCustom
                                title="Grupo"
                                id="nome"
                                value={formik.values.nome}
                                onChange={(value: string) => formik.setFieldValue("nome", value)}
                                error={formik.touched.nome && formik.errors.nome}
                                disabled={props.isView}
                                placeholder="Nome do grupo"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Matriz de Permissões */}
            <div className="mb-6">
                <div className="flex items-center mb-4">
                    <span className="text-blue-600 mr-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                    </span>
                    <h3 className="text-lg font-bold text-gray-800">Matriz de Permissões</h3>
                </div>

                {permissionsData.map(category => (
                    <div key={category.category} className="mb-8">
                        <div className="flex items-center mb-3 ml-1">
                            <span className="text-gray-400 mr-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                                </svg>
                            </span>
                            <h4 className="text-md font-bold text-gray-700">{category.category}</h4>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {category.modules.map((module: { name: string; key: string; allowedActions?: string[]; extraActions?: { label: string; key: string }[] }) => {
                                const allowed = module.allowedActions;
                                const actions = allowed 
                                    ? defaultPermissionTypes.filter(pt => allowed.includes(pt.key))
                                    : defaultPermissionTypes;
                                
                                const allActions = module.extraActions 
                                    ? [...actions, ...module.extraActions]
                                    : actions;

                                return (
                                <div key={module.key} className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                                    <div className="flex justify-between items-start mb-4">
                                        <span className="font-bold text-gray-800 text-sm">{module.name}</span>
                                        <span className="bg-blue-50 text-blue-600 text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider">Módulo</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-4 gap-x-4">
                                        {allActions.map(perm => (
                                            <div key={perm.key} className="flex flex-col">
                                                <span className="text-xs text-gray-500 mb-1 font-medium">{perm.label}</span>
                                                <Switch 
                                                    checked={formik.values.permissions[`${module.key}_${perm.key}`] || false}
                                                    onChange={(checked) => formik.setFieldValue(`permissions.${module.key}_${perm.key}`, checked)}
                                                    disabled={props.isView}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )})}
                        </div>
                    </div>
                ))}
            </div>

            {/* Justificativa */}
            <div className="mb-8">
                <div className="flex items-center mb-2">
                    <span className="text-blue-600 mr-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                    </span>
                    <h3 className="text-lg font-bold text-gray-800">Justificativa</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4 ml-7">Informe o motivo da criação ou alteração deste grupo.</p>
                
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <InputCustom
                        title="Justificativa"
                        id="justificativa"
                        value={formik.values.justificativa}
                        onChange={(value: string) => formik.setFieldValue("justificativa", value)}
                        error={formik.touched.justificativa && formik.errors.justificativa}
                        disabled={props.isView}
                        placeholder="Justificativa"
                    />
                </div>
            </div>

        </div>

        <div className="w-full h-14 flex items-center justify-end bg-[#FFFFFF] shadow-xl px-5">
            <button
            type="button"
            className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#F9FAFA] text-sm text-[#000000] font-bold rounded-full mr-2 hover:bg-gray-100 transition-colors"
            style={{ border: "1px solid #DBDEDF" }}
            onClick={props.onClose}
            id="btnCancel"
            >
            Cancelar
            </button>
            {!props.isView && (
                <button
                type="button"
                className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#0A4984] text-sm text-[#fff] font-bold rounded-full mr-2 hover:bg-[#083a6a] transition-colors"
                onClick={() => formik.handleSubmit()}
                id="btnSave"
                >
                Salvar
                </button>
            )}
        </div>
      </form>
    </div>
  );
};

export default Form;
